import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Sprout, ArrowRight, Check, Loader2 } from 'lucide-react';

/* Coming Soon — Seedz Gateway · Ledger CE-SITE-02 · v1 (ACTIVE)
   Email-gated Seedz (name+email -> /api/subscribe/seedz) + "The Plant — Coming Soon" countdown. QR/funnel destination. */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── EDIT ME: pre-go-live date (placeholder). Change to the real launch date. ──
const GO_LIVE_DATE = new Date('2026-08-22T09:00:00-04:00');
const GO_LIVE_LABEL = 'August 22, 2026';

const pad = (n) => String(n).padStart(2, '0');

const useCountdown = (target) => {
  const [left, setLeft] = useState(() => Math.max(0, target - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, target - Date.now())), 1000);
    return () => clearInterval(id);
  }, [target]);
  const s = Math.floor(left / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
    done: left <= 0,
  };
};

const Ember = ({ i }) => {
  const dur = 6 + (i % 5) * 2;
  const delay = (i % 7) * 0.9;
  const leftPct = (i * 37) % 100;
  return (
    <motion.span
      className="absolute rounded-full"
      style={{ left: `${leftPct}%`, bottom: -10, width: 3 + (i % 3), height: 3 + (i % 3), background: i % 2 ? '#e8c87a' : '#d4a254', opacity: 0.5 }}
      initial={{ y: 0, opacity: 0 }}
      animate={{ y: -700, opacity: [0, 0.7, 0] }}
      transition={{ duration: dur, delay, repeat: Infinity, ease: 'easeOut' }}
    />
  );
};

const ComingSoon = () => {
  const t = useCountdown(GO_LIVE_DATE.getTime());
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | success | error
  const [msg, setMsg] = useState('');

  const submit = useCallback(async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim();
    const cleanName = name.trim();
    if (!cleanName) { setState('error'); setMsg('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setState('error'); setMsg('Please enter a valid email address.'); return;
    }
    setState('loading');
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscribe/seedz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: cleanName, email: cleanEmail }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setState('success');
        setMsg(data.message || 'Check your inbox — your Seedz are on the way! 🌱');
      } else {
        setState('error');
        setMsg((data && data.detail) || 'Please enter a valid email and try again.');
      }
    } catch (err) {
      setState('error');
      setMsg('Something went wrong — please try again.');
    }
  }, [email, name]);

  const units = [
    { label: 'Days', value: t.days },
    { label: 'Hours', value: t.hours },
    { label: 'Minutes', value: t.minutes },
    { label: 'Seconds', value: t.seconds },
  ];

  return (
    <div
      data-testid="coming-soon-page"
      className="relative min-h-screen overflow-hidden flex items-center justify-center px-6 py-16"
      style={{ background: 'radial-gradient(1200px 700px at 50% -10%, #221436 0%, #0b0710 55%, #070409 100%)', fontFamily: 'Georgia, serif' }}
    >
      {/* embers */}
      <div className="pointer-events-none absolute inset-0">
        {Array.from({ length: 24 }, (_, i) => <Ember key={i} i={i} />)}
      </div>

      <div className="relative w-full max-w-3xl text-center">
        {/* eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="uppercase tracking-[0.4em] text-[11px] mb-6"
          style={{ color: '#9c927e', fontFamily: 'Arial, sans-serif' }}
        >
          World of INSPIYAH · Book of Colorz
        </motion.p>

        {/* cover */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.1 }}
          className="mx-auto mb-8 w-40 sm:w-48"
        >
          <div className="rounded-2xl p-2" style={{ background: 'linear-gradient(160deg,#d4a254,#7a5a22)', boxShadow: '0 20px 60px rgba(212,162,84,0.25)' }}>
            <img src="/book-of-colorz-cover.png" alt="Book of Colorz" className="w-full rounded-xl" data-testid="coming-soon-cover" />
          </div>
        </motion.div>

        {/* headline */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight"
          style={{ color: '#e8c87a' }}
        >
          Something sacred<br />is taking root.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-5 text-lg italic"
          style={{ color: '#e9e2d4' }}
          data-testid="coming-soon-tagline"
        >
          &ldquo;Are we planting or sifting?&rdquo;
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.45 }}
          className="mt-4 mx-auto max-w-xl text-sm sm:text-base"
          style={{ color: '#9c927e', fontFamily: 'Arial, sans-serif', lineHeight: 1.6 }}
        >
          The free <b style={{ color: '#e9e2d4' }}>Seedz</b> — 7 unique coloring experiences to color for
          <b style={{ color: '#e9e2d4' }}> YAH · Health · Wealth · Happiness</b> — are ready. Enter your details and we&rsquo;ll email them straight to you.
        </motion.p>

        {/* countdown */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-9 flex items-center justify-center gap-3 sm:gap-4"
          data-testid="coming-soon-countdown"
        >
          {units.map((u) => (
            <div key={u.label} className="flex flex-col items-center">
              <div
                className="w-16 sm:w-20 py-3 rounded-xl text-2xl sm:text-3xl font-semibold tabular-nums"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(212,162,84,0.35)', color: '#e8c87a', fontFamily: 'Arial, sans-serif' }}
              >
                {pad(u.value)}
              </div>
              <span className="mt-2 text-[10px] uppercase tracking-widest" style={{ color: '#9c927e', fontFamily: 'Arial, sans-serif' }}>{u.label}</span>
            </div>
          ))}
        </motion.div>
        <p className="mt-4 text-xs" style={{ color: '#9c927e', fontFamily: 'Arial, sans-serif' }}>
          <b style={{ color: '#e8c87a' }}>The Plant</b> · 1st INSPIYAH Edition paperback arrives · <b style={{ color: '#e8c87a' }}>{GO_LIVE_LABEL}</b>
        </p>

        {/* email capture */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65 }}
          className="mt-10 mx-auto max-w-md"
        >
          {state === 'success' ? (
            <div
              data-testid="coming-soon-success"
              className="flex items-center justify-center gap-2 rounded-xl px-5 py-4 text-sm"
              style={{ background: 'rgba(127,212,138,0.1)', border: '1px solid rgba(127,212,138,0.5)', color: '#bfe9c6', fontFamily: 'Arial, sans-serif' }}
            >
              <Check size={16} /> {msg}
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3">
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: '#e9e2d4', fontFamily: 'Arial, sans-serif' }}
                data-testid="coming-soon-name-input"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="flex-1 px-4 py-3 rounded-xl text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: '#e9e2d4', fontFamily: 'Arial, sans-serif' }}
                  data-testid="coming-soon-email-input"
                />
                <button
                  type="submit"
                  disabled={state === 'loading'}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-transform hover:scale-[1.02] disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg,#e8c87a,#d4a254)', color: '#241a07', fontFamily: 'Arial, sans-serif' }}
                  data-testid="coming-soon-submit"
                >
                  {state === 'loading' ? <Loader2 size={16} className="animate-spin" /> : <Sprout size={16} />}
                  {state === 'loading' ? 'Planting…' : 'Send My Seedz'}
                  {state !== 'loading' && <ArrowRight size={15} />}
                </button>
              </div>
            </form>
          )}
          {state === 'error' && (
            <p className="mt-3 text-xs" style={{ color: '#e57373', fontFamily: 'Arial, sans-serif' }}>{msg}</p>
          )}
          <p className="mt-3 text-[11px]" style={{ color: '#6f6857', fontFamily: 'Arial, sans-serif' }}>
            No spam. Your Seedz link is <b style={{ color: '#9c927e' }}>emailed</b> to you — so use a real address. 🌱
          </p>
        </motion.div>

        {/* footer */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-14"
        >
          <p className="text-lg font-semibold" style={{ color: '#e8c87a', letterSpacing: '0.04em' }}>
            Where Colorz Unite Us!
          </p>
          <p className="mt-2 text-[11px]" style={{ color: '#6f6857', fontFamily: 'Arial, sans-serif' }}>
            &ldquo;&amp; you will know the truth &amp; the truth will set you free.&rdquo; — John 8:32
          </p>

          {/* legal quicklinks — required on every ecosystem property (ECOSYSTEM_DEPLOYMENT_STRATEGY / REDUNDANCY_PLAN §5) */}
          <nav
            data-testid="coming-soon-legal-footer"
            className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[11px]"
            style={{ fontFamily: 'Arial, sans-serif' }}
          >
            {[
              { label: 'Privacy', href: '/privacy-policy', testid: 'legal-privacy' },
              { label: 'Data Processing', href: '/data-processing-addendum', testid: 'legal-dpa' },
              { label: 'Terms', href: '/terms-of-service', testid: 'legal-terms' },
              { label: 'AI Usage', href: '/ai-usage-policy', testid: 'legal-ai-usage' },
              { label: 'Disclaimer', href: '/disclaimer', testid: 'legal-disclaimer' },
              { label: 'FAQ', href: '/faq', testid: 'legal-faq' },
            ].map((l, i, arr) => (
              <React.Fragment key={l.testid}>
                <a
                  href={l.href}
                  data-testid={l.testid}
                  className="transition-colors hover:underline"
                  style={{ color: '#9c927e' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#e8c87a')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#9c927e')}
                >
                  {l.label}
                </a>
                {i < arr.length - 1 && <span style={{ color: '#4d483d' }}>·</span>}
              </React.Fragment>
            ))}
          </nav>

          <p className="mt-4 text-[10px]" style={{ color: '#4d483d', fontFamily: 'Arial, sans-serif' }}>
            © 2026 INSPIYAH · Book of Colorz™ · Where Colorz Unite Us™
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ComingSoon;
